import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

import api from '@/client/core/api'
import LocalStorage from '@/client/core/local_storage'
import { setNttAaaSwitchoverAt } from '@/common/utils/ntt_aaa_switchover'
import {
  EMERGENCY_NOTIFICATION_ID,
  EmergencyNotificationResponse,
  emergencyNotificationResponseSchema
} from '@/typings/emergency_notifications'

export interface User {
  id: number
  name: string
  pictureUrl: string
  email: string
  isSystemAdmin: number
  isTwoFactor: number
  created: string
  isFirstDisplay: number
}

export interface Group {
  id: number
  name: string
  contractStatus: number
  contractConfirmAt: string
  isUserAdmin?: number
  isContractManager?: number
  allowInstantwin?: number
  allowCreateInstantwin?: number
  allowWebInstantwin?: number
  isBenchmarkAnalyticsCsvDownload?: number
  allowBenchmarkAnalytics?: number
  allowInInstantwin?: number
  currentMonthlyApplicantCount?: number
  currentMonthlyDmCount?: number
  maxMonthlyApplicantCount?: number
  maxMonthlyDmCount?: number
  currentCampaignCount?: number
  maxCampaignCount?: number
  currentInstantwinCount?: number
  maxInstantwinCount?: number
  currentWebInstantwinCount?: number
  maxWebInstantwinCount?: number
  currentInInstantwinCount?: number
  maxInInstantwinCount?: number
}

export interface SystemState {
  user: User | null
  currentGroup: Group | null
  groups: Group[]
  guestGroups: Group[]
  language: string
  loading: boolean
  emergencyNotification: EmergencyNotificationResponse | null
}

type MeGroupDto = {
  id: number
  name: string
  contractStatus: number
  allowInstantwin: number
  allowCreateInstantwin: number
  allowWebInstantwin: number
  isBenchmarkAnalyticsCsvDownload: number
  allowBenchmarkAnalytics: number
  contractConfirmAt: string
}

type MeGroupUserMapDto = {
  isAdmin: number
  isContractManager: number
  group: MeGroupDto
}

type MeGroupGuestUserMapDto = {
  group: MeGroupDto
}

type MeResponse = {
  id: number
  name: string
  pictureUrl: string
  email: string
  isSystemAdmin: number
  isTwoFactor: number
  created: string
  isShowCampaignCreateIntro: number
  nttAaaSwitchoverAt: string
  isShowEmergencyNotification: boolean
  group_user_maps: MeGroupUserMapDto[]
  group_guest_user_maps: MeGroupGuestUserMapDto[]
}

export const useSystemStore = defineStore('systemStore', () => {
  const user = ref<User | null>(null)
  const currentGroup = ref<Group | null>(null)
  const groups = ref<Group[]>([])
  const guestGroups = ref<Group[]>([])
  const language = ref<string>('')
  const loading = ref<boolean>(false)
  const emergencyNotification = ref<EmergencyNotificationResponse | null>(null)

  const currentGroupId = computed<number | null>(() => currentGroup.value?.id ?? null)

  const requireCurrentGroupId = (): number => {
    if (typeof currentGroupId.value === 'number') {
      return currentGroupId.value
    }

    throw new Error('system.currentGroup.id is required')
  }

  const applyCurrentGroup = (groupId: number | null): void => {
    if (!user.value || groups.value.length + guestGroups.value.length === 0) return

    const next =
      groups.value.find(group => group.id === groupId) ||
      guestGroups.value.find(group => group.id === groupId) ||
      groups.value.at(0) ||
      guestGroups.value.at(0)

    if (!next) return

    const currentGroupLocal = LocalStorage.getCurrentGroup() || {}
    currentGroupLocal[user.value.id] = next.id
    LocalStorage.setCurrentGroup(currentGroupLocal)

    currentGroup.value = next
  }

  const setUser = async (payload: User | null): Promise<void> => {
    user.value = payload
  }

  const setGroups = async (payload: Group[]): Promise<void> => {
    groups.value = payload
  }

  const setGuestGroups = async (payload: Group[]): Promise<void> => {
    guestGroups.value = payload
  }

  const setCurrentGroup = async (groupId: number | null): Promise<void> => {
    applyCurrentGroup(groupId)
  }

  const resetUser = async (): Promise<void> => {
    user.value = null
    groups.value = []
    guestGroups.value = []
    currentGroup.value = null
    emergencyNotification.value = null
  }

  const setLanguage = async (value: string): Promise<void> => {
    language.value = value
  }

  const setLoading = async (value: boolean): Promise<void> => {
    loading.value = value
  }

  const fetchUserData = async (): Promise<boolean | void> => {
    let userData: MeResponse | null = null

    try {
      const result = await api.get('/me')

      if (!result.data) {
        return false
      }
      userData = result.data as MeResponse
    } catch {
      return false
    }

    setNttAaaSwitchoverAt(userData.nttAaaSwitchoverAt)

    user.value = {
      id: userData.id,
      name: userData.name,
      pictureUrl: userData.pictureUrl,
      email: userData.email,
      isSystemAdmin: userData.isSystemAdmin,
      isTwoFactor: userData.isTwoFactor,
      created: userData.created,
      isFirstDisplay: userData.isShowCampaignCreateIntro
    }

    groups.value = userData.group_user_maps.map(v => ({
      id: v.group.id,
      name: v.group.name,
      isUserAdmin: v.isAdmin,
      contractStatus: v.group.contractStatus,
      allowInstantwin: v.group.allowInstantwin,
      allowCreateInstantwin: v.group.allowCreateInstantwin,
      allowWebInstantwin: v.group.allowWebInstantwin,
      isBenchmarkAnalyticsCsvDownload: v.group.isBenchmarkAnalyticsCsvDownload,
      allowBenchmarkAnalytics: v.group.allowBenchmarkAnalytics,
      contractConfirmAt: v.group.contractConfirmAt,
      isContractManager: v.isContractManager
    }))

    guestGroups.value = userData.group_guest_user_maps.map(v => ({
      id: v.group.id,
      name: v.group.name,
      contractStatus: v.group.contractStatus,
      contractConfirmAt: v.group.contractConfirmAt
    }))

    let nextGroupId: number | null = null
    const currentGroupLocal = LocalStorage.getCurrentGroup()
    if (currentGroupLocal && currentGroupLocal[userData.id]) {
      nextGroupId = currentGroupLocal[userData.id]
    }

    applyCurrentGroup(nextGroupId)

    if (userData.isShowEmergencyNotification) {
      // お知らせの取得はログイン処理を待たせない.
      void fetchEmergencyNotification()
    }
  }

  /**
   * お知らせを取得する.
   */
  const fetchEmergencyNotification = async (): Promise<void> => {
    const requestedUserId = user.value?.id ?? null // 取得を待たずに進めるため、応答が返るまでにログアウトや別ユーザーへの切り替えが起きうる

    try {
      const result = await api.get(`/emergency_notifications/${EMERGENCY_NOTIFICATION_ID}`)
      const parsed = emergencyNotificationResponseSchema.safeParse(result?.data)

      if (!parsed.success) return
      if (user.value?.id !== requestedUserId) return

      emergencyNotification.value = parsed.data
    } catch {
      // お知らせの取得に失敗してもログイン処理は止めない
    }
  }

  /**
   * お知らせを既読にして閉じる.
   */
  const closeEmergencyNotification = async (): Promise<void> => {
    emergencyNotification.value = null

    const groupId = currentGroupId.value

    if (groupId === null) return

    await api.post(`/emergency_notifications/?groupId=${groupId}`, {
      emergency_notification_id: EMERGENCY_NOTIFICATION_ID
    })
  }

  const getCurrentGroup = async (): Promise<void> => {
    const groupId = currentGroup.value ? currentGroup.value.id : null

    let group = {}

    try {
      if (!groupId) return

      const result = await api.get(`/groups/${groupId}`)

      if (!result.data) return

      group = result.data
    } catch {
      return
    }

    currentGroup.value = Object.assign({}, currentGroup.value, group) as Group
  }

  return {
    user: readonly(user),
    currentGroup: readonly(currentGroup),
    currentGroupId,
    groups: readonly(groups),
    guestGroups: readonly(guestGroups),
    language: readonly(language),
    loading: readonly(loading),
    emergencyNotification: readonly(emergencyNotification),
    requireCurrentGroupId,
    setUser,
    setGroups,
    setGuestGroups,
    setCurrentGroup,
    resetUser,
    setLanguage,
    setLoading,
    fetchUserData,
    getCurrentGroup,
    fetchEmergencyNotification,
    closeEmergencyNotification
  }
})
